import React, { useState } from 'react';
import { CompleteKundali, Language, ChartStyle } from '../types/kundali';
import { BrihatJanmapatrikaReport, DossierScope, PrintTheme } from './BrihatJanmapatrikaReport';
import { HtmlPrintPreviewModal } from './HtmlPrintPreviewModal';
import { downloadElementAsPdf, PaperSize } from '../utils/pdfExportUtil';
import { getSavedAstrologerBranding, AstrologerBranding } from '../lib/astrologerBrandingService';
import { SUPPORTED_LANGUAGES } from '../constants/languages';
import {
  Printer,
  Download,
  Globe,
  Sparkles,
  ExternalLink,
  Loader2,
  BookOpen,
  Layers
} from 'lucide-react';

interface PublicationDossierViewProps {
  kundali: CompleteKundali;
  lang: Language;
  chartStyle?: ChartStyle;
}

export const PublicationDossierView: React.FC<PublicationDossierViewProps> = ({
  kundali,
  lang: initialLang = 'hi',
  chartStyle: initialChartStyle = 'north'
}) => {
  const [selectedLang, setSelectedLang] = useState<Language>(initialLang || 'hi');
  const [reportScope, setReportScope] = useState<DossierScope>('BRIHAT');
  const [activeGrantha, setActiveGrantha] = useState<number | 'ALL'>('ALL');
  const [paperSize, setPaperSize] = useState<PaperSize>('a4');
  const [printTheme, setPrintTheme] = useState<PrintTheme>('deluxe');
  const [chartStyle, setChartStyle] = useState<ChartStyle>(initialChartStyle);
  const [branding] = useState<AstrologerBranding>(() => getSavedAstrologerBranding());

  const [isExportingPdf, setIsExportingPdf] = useState<boolean>(false);
  const [pdfProgressText, setPdfProgressText] = useState<string>('');
  const [isHtmlPreviewOpen, setIsHtmlPreviewOpen] = useState<boolean>(false);
  const [notice, setNotice] = useState<string | null>(null);

  const isHi = selectedLang === 'hi' || selectedLang === 'sa';

  const getCleanReportTitle = () => {
    const cleanName = (kundali.birthDetails?.name || 'Native').replace(/[^a-zA-Z0-9_\u0900-\u097F]/g, '_');
    const granthaTag = activeGrantha === 'ALL' ? 'All_Granthas' : `Grantha_${activeGrantha}`;
    const scopeTag = reportScope === 'GRAND' ? `Bhrigu_Samhita_${granthaTag}` : reportScope === 'BRIHAT' ? 'Brihat_Patrika' : `${reportScope}_Dossier`;
    const themeTag = printTheme === 'eco-bw' ? 'EcoBW' : 'Deluxe';
    const paperTag = paperSize.toUpperCase();
    return `${cleanName}_${scopeTag}_${paperTag}_${themeTag}`;
  };

  const handleDownloadPdf = async () => {
    setIsExportingPdf(true);
    setNotice(null);
    try {
      await downloadElementAsPdf(
        'brihat-janmapatrika-container',
        getCleanReportTitle(),
        (msg) => setPdfProgressText(msg),
        paperSize
      );
      setNotice(isHi ? 'पीडीएफ सफलतापूर्वक डाउनलोड हो गया!' : 'PDF dossier downloaded successfully!');
      setTimeout(() => setNotice(null), 4000);
    } catch (err) {
      console.error(err);
      setNotice('PDF download encountered browser limits. Use HTML Print Tool below.');
    } finally {
      setIsExportingPdf(false);
      setPdfProgressText('');
    }
  };

  const handlePhysicalPrint = () => {
    window.print();
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16 animate-fadeIn">
      {/* Top Header Card */}
      <div className="bg-astro-card border border-astro-border rounded-3xl p-6 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <Printer className="w-48 h-48 text-astro-primary" />
        </div>

        <div className="relative z-10 flex flex-wrap items-center justify-between gap-4">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-astro-primary/10 text-astro-primary text-xs font-bold">
              <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
              <span>{isHi ? 'समर्पित प्रकाशन डॉसियर एवं प्रिंट टूल' : 'Dedicated Publication & Print Tool'}</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold font-serif text-astro-text">
              {isHi ? 'वृहत् जन्मपत्रिका प्रकाशन एवं एचटीएमएल प्रिंट स्टूडियो' : 'Brihat Janmapatrika Publication & HTML Print Studio'}
            </h1>
            <p className="text-xs sm:text-sm text-astro-secondary-text max-w-2xl">
              {isHi
                ? 'कंटेंट-अवेयर पेजिनेशन, सटीक फौंट साइज, षोडशवर्ग, दशा टाइमिंग और शास्त्रीय फलादेश के साथ पूर्ण पेशेवर प्रकाशन डॉसियर।'
                : 'Professional publication dossier with content-aware pagination, font floor enforcement, divisional charts, and in-app HTML print preview.'}
            </p>
          </div>

          {/* Top Action Toolbar */}
          <div className="flex flex-wrap items-center gap-2.5">
            <button
              onClick={handleDownloadPdf}
              disabled={isExportingPdf}
              className="px-4 py-2.5 rounded-xl bg-astro-primary hover:bg-astro-secondary text-white text-xs font-bold flex items-center gap-2 shadow-md transition-all cursor-pointer active:scale-95 disabled:opacity-60"
            >
              {isExportingPdf ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-astro-accent" />
                  <span>{pdfProgressText || 'Generating...'}</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 text-astro-accent" />
                  <span>{isHi ? 'पीडीएफ डाउनलोड' : 'Download PDF'}</span>
                </>
              )}
            </button>

            <button
              onClick={() => setIsHtmlPreviewOpen(true)}
              className="px-4 py-2.5 rounded-xl bg-amber-700 hover:bg-amber-800 text-white text-xs font-bold flex items-center gap-2 shadow-md transition-all cursor-pointer active:scale-95"
            >
              <ExternalLink className="w-4 h-4" />
              <span>{isHi ? 'एचटीएमएल प्रिंट टूल' : 'HTML Print Tool'}</span>
            </button>

            <button
              onClick={handlePhysicalPrint}
              className="px-4 py-2.5 rounded-xl bg-astro-secondary-surface hover:bg-astro-card border border-astro-border text-astro-secondary-text text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer active:scale-95"
            >
              <Printer className="w-4 h-4" />
              <span>{isHi ? 'प्रिंट' : 'System Print'}</span>
            </button>
          </div>
        </div>

        {/* Configuration Bar */}
        <div className="mt-6 pt-6 border-t border-astro-border grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 gap-3 text-xs">
          {/* Scope */}
          <div>
            <label className="block font-bold text-astro-secondary-text mb-1">{isHi ? 'रिपोर्ट स्कोप' : 'Report Scope'}:</label>
            <select
              value={reportScope}
              onChange={(e) => setReportScope(e.target.value as DossierScope)}
              className="w-full p-2 rounded-xl border border-astro-border bg-astro-bg text-astro-text font-semibold cursor-pointer"
            >
              <option value="BRIHAT">{isHi ? 'वृहत् जन्मपत्रिका (12 Pages)' : 'Brihat Janmapatrika'}</option>
              <option value="GRAND">{isHi ? 'महा संहिता (Grand Granthas)' : 'Grand Samhita (All Granthas)'}</option>
              <option value="CAREER">{isHi ? 'कर्म एवं अर्थ संपदा' : 'Career & Wealth'}</option>
              <option value="MARRIAGE">{isHi ? 'विवाह एवं दाम्पत्य' : 'Marriage & Family'}</option>
              <option value="REMEDIAL">{isHi ? 'लाल किताब एवं उपाय' : 'Remedial & Lal Kitab'}</option>
              <option value="SUMMARY">{isHi ? 'संक्षिप्त जन्मपत्रिका' : 'Summary Dossier'}</option>
            </select>
          </div>

          {/* Grantha Select (if GRAND) */}
          {reportScope === 'GRAND' && (
            <div>
              <label className="block font-bold text-astro-secondary-text mb-1">{isHi ? 'ग्रन्थ चयन' : 'Grantha'}:</label>
              <select
                value={activeGrantha}
                onChange={(e) => setActiveGrantha(e.target.value === 'ALL' ? 'ALL' : Number(e.target.value))}
                className="w-full p-2 rounded-xl border border-astro-border bg-astro-bg text-astro-text font-semibold cursor-pointer"
              >
                <option value="ALL">All Granthas (Complete)</option>
                <option value={1}>Grantha 1 (Janma Ganita)</option>
                <option value={2}>Grantha 2 (Shodashvarga)</option>
                <option value={3}>Grantha 3 (Bala & Ashtakavarga)</option>
                <option value={4}>Grantha 4 (Multi-Dasha)</option>
                <option value={5}>Grantha 5 (Phaladesh)</option>
                <option value={6}>Grantha 6 (Lal Kitab)</option>
                <option value={7}>Grantha 7 (Varshaphala)</option>
                <option value={8}>Grantha 8 (Gemology & Muhurta)</option>
              </select>
            </div>
          )}

          {/* Language */}
          <div>
            <label className="block font-bold text-astro-secondary-text mb-1">{isHi ? 'भाषा' : 'Language'}:</label>
            <select
              value={selectedLang}
              onChange={(e) => setSelectedLang(e.target.value as Language)}
              className="w-full p-2 rounded-xl border border-astro-border bg-astro-bg text-astro-text font-semibold cursor-pointer"
            >
              {SUPPORTED_LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.native} ({l.label})</option>
              ))}
            </select>
          </div>

          {/* Paper Size */}
          <div>
            <label className="block font-bold text-astro-secondary-text mb-1">{isHi ? 'पेपर आकार' : 'Paper Size'}:</label>
            <select
              value={paperSize}
              onChange={(e) => setPaperSize(e.target.value as PaperSize)}
              className="w-full p-2 rounded-xl border border-astro-border bg-astro-bg text-astro-text font-semibold cursor-pointer"
            >
              <option value="a4">A4 Standard</option>
              <option value="letter">US Letter</option>
              <option value="legal">US Legal</option>
              <option value="a3">A3 Poster</option>
            </select>
          </div>

          {/* Print Theme */}
          <div>
            <label className="block font-bold text-astro-secondary-text mb-1">{isHi ? 'थीम' : 'Print Theme'}:</label>
            <select
              value={printTheme}
              onChange={(e) => setPrintTheme(e.target.value as PrintTheme)}
              className="w-full p-2 rounded-xl border border-astro-border bg-astro-bg text-astro-text font-semibold cursor-pointer"
            >
              <option value="deluxe">Deluxe Vedic (Gold & Teal)</option>
              <option value="eco-bw">Eco B&W (Printer Friendly)</option>
            </select>
          </div>

          {/* Chart Style */}
          <div>
            <label className="block font-bold text-astro-secondary-text mb-1">{isHi ? 'कुंडली शैली' : 'Chart Style'}:</label>
            <select
              value={chartStyle}
              onChange={(e) => setChartStyle(e.target.value as ChartStyle)}
              className="w-full p-2 rounded-xl border border-astro-border bg-astro-bg text-astro-text font-semibold cursor-pointer"
            >
              <option value="north">North Indian</option>
              <option value="south">South Indian</option>
              <option value="east">East Indian</option>
            </select>
          </div>
        </div>

        {notice && (
          <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-xs font-bold text-center">
            {notice}
          </div>
        )}
      </div>

      {/* Live Dossier Publication View Container */}
      <div className="bg-astro-secondary-surface/40 p-4 sm:p-8 rounded-3xl border border-astro-border flex justify-center shadow-inner overflow-x-auto">
        <BrihatJanmapatrikaReport
          kundali={kundali}
          lang={selectedLang}
          branding={branding}
          chartStyle={chartStyle}
          reportScope={reportScope}
          paperSize={paperSize}
          printTheme={printTheme}
          activeGrantha={activeGrantha}
        />
      </div>

      {/* In-App HTML Print Tool Modal */}
      <HtmlPrintPreviewModal
        isOpen={isHtmlPreviewOpen}
        onClose={() => setIsHtmlPreviewOpen(false)}
        elementId="brihat-janmapatrika-container"
        documentTitle={getCleanReportTitle()}
        paperSize={paperSize}
      />
    </div>
  );
};
